/* ============================================================
   YU — QUIZ ENGINE
   yu-translation-spec.md v4 §5.1, operator ruling 2.

   DATA, NOT LOGIC. This file fetches assets/yu-quiz-matrix.json at runtime
   and contains ZERO question text, concern names, SKU codes, track names,
   and zero result copy. It never special-cases any concern by name. A new
   store swaps one JSON file; this engine is untouched.

   Routing is layered and read entirely from matrix.routing:
     Q1 sets the track  ->  routing.track_by
     Q2 re-routes it    ->  routing.reroute_by   (most-specific route wins)
     Q4 sets the count  ->  routing.count_by     -> routing.count_buckets
     Q5 sets the lead   ->  routing.lead_by      -> routing.lead[*].prefer
     Q3 breaks ties     ->  routing.tiebreak_by  -> routing.tiebreak_order

   Track selection is a generic route match: every key in a track's `route`
   object must equal the corresponding answer, and the track matching the
   MOST keys wins. That is what makes the age-band re-route fall out of the
   data rather than out of a branch named for a concern.

   Weekly-mask add-on (ruling 2): rendered when, and only when, the selected
   track carries weekly_mask data AND the routine resolves to
   routing.addon_bucket. Both come from the matrix — there is no bucket
   literal here.

   RESULTS (R4-2). Result cards are real product cards — image, title,
   price, ingredient badge — every one of those facts read from the PRODUCT
   MAP that quiz.liquid renders server-side into the page. The engine never
   fetches a product and never derives one: the matrix says WHICH products,
   the map says what they currently look like and cost.

   Every card is selectable and starts selected. "Shop your routine" posts
   every selected line in ONE /cart/add.js call by submitting a real
   [data-yu-add-form], which hands the request to yu-theme.js — the same
   bundled section-rendering path the rest of the theme uses, so the drawer
   re-renders and opens from the server's markup. Zero selected disables
   the button.

   STORAGE: none. Answers live in memory for the session only. No cookie,
   no account write. `yu_quizbar_dismissed` (owned by yu-theme.js) remains
   the only localStorage key in the theme.

   CURRENCY: no price is formatted here and no currency symbol appears in
   this file. Prices arrive from the map already rendered through | money.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-yu-quiz]');
  if (!root) return;

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  /* Verified against Klaviyo's live OpenAPI spec at build time
     (openapi/stable/apis/create_client_subscription.json): path
     /client/subscriptions, revision header default 2026-07-15,
     Content-Type application/vnd.api+json, company_id as a query param. */
  var KLAVIYO_REVISION = '2026-07-15';

  var cfg = {
    matrixUrl: root.getAttribute('data-matrix-url') || '',
    publicKey: root.getAttribute('data-klaviyo-key') || '',
    listId: root.getAttribute('data-klaviyo-list') || '',
    gateEnabled: root.getAttribute('data-gate-enabled') === 'true',
    cartAddUrl: root.getAttribute('data-cart-add-url') || ''
  };

  /* Every visible string is chrome supplied by the section. None of it is
     quiz content, and none of it is written into this file. */
  function copy(name) { return root.getAttribute('data-' + name) || ''; }

  var matrix = null;
  var answers = {};      /* questionId -> option value, in memory only */
  var step = 0;          /* 0..n-1 questions, then gate, then results */
  var gateDone = false;

  /* Server-rendered catalogue facts, keyed by handle. Parsed once, before
     the first render, and never refetched. */
  var products = readProductMap();
  var currentResult = null;   /* cached so a selection toggle never rebuilds */
  var selected = {};          /* handle -> bool */
  var cartFields = null;
  var cartButton = null;

  function readProductMap() {
    var node = document.querySelector('[data-yu-quiz-products]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (err) {
      /* A malformed map must not take the quiz down: results still render
         with the titles the matrix carries. */
      return {};
    }
  }

  /* ---------- tiny DOM helpers ---------- */
  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }
  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* ============================================================
     ROUTING — every rule below reads from matrix.routing
     ============================================================ */

  function selectTrack() {
    var best = null;
    var bestScore = -1;
    for (var i = 0; i < matrix.tracks.length; i++) {
      var track = matrix.tracks[i];
      var route = track.route || {};
      var keys = Object.keys(route);
      var matched = 0;
      var ok = true;
      for (var k = 0; k < keys.length; k++) {
        var qid = questionIdFor(keys[k]);
        if (!qid || answers[qid] !== route[keys[k]]) { ok = false; break; }
        matched++;
      }
      /* Most-specific match wins: the age-banded routes carry two keys, the
         plain concern routes carry one. No concern is named here. */
      if (ok && matched > bestScore) {
        best = track;
        bestScore = matched;
      }
    }
    return best;
  }

  /* Routes are keyed by short ids ("q1", "q2"); questions by complete ids
     ("q1_concern"). Bridge them positionally from question_order. */
  function questionIdFor(shortKey) {
    var order = matrix.question_order || [];
    for (var i = 0; i < order.length; i++) {
      if (order[i].indexOf(shortKey) === 0) return order[i];
    }
    return null;
  }

  function selectBucket() {
    var r = matrix.routing;
    var answer = answers[r.count_by];
    return (r.count_buckets || {})[answer] || null;
  }

  function applyLeadAndTiebreak(products) {
    var r = matrix.routing;
    var list = products.slice();
    if (list.length < 2) return list;

    var leadCfg = (r.lead || {})[answers[r.lead_by]] || {};
    var prefer = leadCfg.prefer || [];

    /* Rank each product by its position in the Q5 preference list. */
    function rank(item) {
      var idx = prefer.indexOf(item.sku);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    }

    /* Stable sort by preference rank. */
    list = list
      .map(function (item, i) { return { item: item, i: i, rank: rank(item) }; })
      .sort(function (a, b) { return a.rank - b.rank || a.i - b.i; })
      .map(function (row) { return row.item; });

    /* Q3 TIEBREAK — only engages when the lead step leaves the top slot
       genuinely tied (two or more products share the best rank). The Q3
       answer's position in routing.tiebreak_order picks among the tied
       candidates deterministically. No concern or SKU is named. */
    var order = r.tiebreak_order || [];
    var tied = list.filter(function (item) { return rank(item) === rank(list[0]); });
    if (tied.length > 1 && order.length) {
      var pos = order.indexOf(answers[r.tiebreak_by]);
      if (pos < 0) pos = 0;
      var chosen = tied[Math.min(pos, tied.length - 1)];
      list = [chosen].concat(list.filter(function (item) { return item !== chosen; }));
    }

    return list;
  }

  function buildResult() {
    var track = selectTrack();
    var bucket = selectBucket();
    if (!track || !bucket) return null;

    var products = applyLeadAndTiebreak((track.counts || {})[bucket] || []);

    /* Weekly-mask add-on: ruling 2. Data-gated on both sides. */
    var addons = [];
    if (bucket === matrix.routing.addon_bucket && (track.weekly_mask || []).length) {
      addons = track.weekly_mask;
    }

    return { track: track, bucket: bucket, products: products, addons: addons };
  }

  /* ============================================================
     KLAVIYO — Client Subscriptions API
     Public key and list id come from section settings and are read off the
     root element's data attributes. No key is hardcoded, and no private key
     exists anywhere in this theme. The SKIP path makes no network call.
     ============================================================ */
  function submitEmail(email) {
    if (!cfg.publicKey || !cfg.listId) return Promise.resolve();

    var props = {};
    var map = (matrix.meta && matrix.meta.klaviyo_properties) || {};
    Object.keys(map).forEach(function (prop) {
      var qid = questionIdFor(map[prop]);
      if (qid && answers[qid]) props[prop] = answers[qid];
    });

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'YU skin quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: props,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: cfg.listId } }
        }
      }
    };

    return fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(cfg.publicKey), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'revision': KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    }).catch(function () {
      /* A capture failure must never block the visitor from their result. */
    });
  }

  /* ============================================================
     RENDER
     ============================================================ */
  function renderProgress(current, total) {
    var wrap = el('div', 'yuk-quiz__progress');
    var bar = el('div', 'yu-progress');
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuenow', String(current));
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', String(total));
    for (var i = 0; i < total; i++) {
      var dot = el('span', 'yu-progress__dot' + (i < current ? ' is-done' : ''));
      bar.appendChild(dot);
    }
    wrap.appendChild(bar);
    return wrap;
  }

  function renderQuestion() {
    var questions = matrix.questions;
    var q = questions[step];
    var card = el('div', 'yuk-quiz__card');

    card.appendChild(renderProgress(step + 1, questions.length));
    var counter = copy('progress-template')
      .replace('[n]', String(step + 1))
      .replace('[total]', String(questions.length));
    card.appendChild(el('p', 'yuk-quiz__step', counter));

    var heading = el('h2', 'yuk-quiz__q', q.prompt);
    card.appendChild(heading);

    var opts = el('div', 'yuk-quiz__opts');
    opts.setAttribute('role', 'group');
    opts.setAttribute('aria-label', q.prompt);

    q.options.forEach(function (option) {
      var selected = answers[q.id] === option.value;
      var btn = el('button', 'yu-qopt' + (selected ? ' is-selected' : ''));
      btn.type = 'button';
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      var mark = el('span', 'yu-qopt__mark');
      mark.setAttribute('aria-hidden', 'true');
      btn.appendChild(mark);
      btn.appendChild(el('span', 'yu-qopt__label', option.label));
      btn.addEventListener('click', function () {
        answers[q.id] = option.value;   /* back-stepping preserves answers */
        currentResult = null;           /* answers moved: rebuild the routine */
        if (step < questions.length - 1) {
          step++;
        } else {
          step = questions.length;
        }
        render();
      });
      opts.appendChild(btn);
    });
    card.appendChild(opts);

    var nav = el('div', 'yuk-quiz__nav');
    if (step > 0) {
      var back = el('button', 'yu-btn yu-btn--text', copy('back-label'));
      back.type = 'button';
      back.addEventListener('click', function () { step--; render(); });
      nav.appendChild(back);
    }
    card.appendChild(nav);
    return card;
  }

  function renderGate() {
    var card = el('div', 'yuk-quiz__card yuk-quiz__gate');
    card.appendChild(renderProgress(matrix.questions.length, matrix.questions.length));
    card.appendChild(el('h2', 'yuk-quiz__q', copy('gate-heading')));
    var body = copy('gate-body');
    if (body) card.appendChild(el('p', null, body));

    var form = el('form', 'yuk-quiz__gateform');
    var field = el('div', 'yu-field');
    var label = el('label', 'yu-field__label', copy('email-label'));
    label.setAttribute('for', 'yu-quiz-email');
    var input = el('input', 'yu-input');
    input.type = 'email';
    input.id = 'yu-quiz-email';
    input.name = 'email';
    input.autocomplete = 'email';
    input.placeholder = copy('email-placeholder');
    field.appendChild(label);
    field.appendChild(input);
    form.appendChild(field);

    var actions = el('div', 'yuk-quiz__gateactions');
    var submit = el('button', 'yu-btn yu-btn--primary', copy('gate-submit'));
    submit.type = 'submit';
    actions.appendChild(submit);

    /* SKIP is a visible, first-class control — never an overlay trick,
       never a required field, never a dark pattern (§5.1). It renders the
       results with NO network call and no silent capture. */
    var skip = el('button', 'yu-btn yu-btn--text', copy('gate-skip'));
    skip.type = 'button';
    skip.addEventListener('click', function () {
      gateDone = true;
      render();
    });
    actions.appendChild(skip);
    form.appendChild(actions);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var value = input.value.trim();
      if (!value) { gateDone = true; render(); return; }
      submit.disabled = true;
      submitEmail(value).then(function () {
        gateDone = true;
        render();
      });
    });

    card.appendChild(form);
    return card;
  }

  /* ============================================================
     RESULT — SELECTION AND CART
     ============================================================ */

  /* A matrix item is purchasable only if the server-rendered map has a
     variant for it and the store says it is available. Anything else still
     renders (the visitor keeps the recommendation and the link) but cannot
     be selected, because there is nothing to add. */
  function facts(item) { return products[item.handle] || {}; }
  function purchasable(item) {
    var info = facts(item);
    return !!(info.id && info.available);
  }

  function initSelection(result) {
    selected = {};
    if (!result) return;
    result.products.concat(result.addons).forEach(function (item) {
      selected[item.handle] = purchasable(item);
    });
  }

  function selectionCount() {
    return Object.keys(selected).filter(function (handle) { return selected[handle]; }).length;
  }

  /* Rebuild the hidden line items and the button's enabled state. The
     button lives outside `cartFields`, so clearing the fields never
     destroys it. */
  function syncCart() {
    if (!cartFields) return;
    clear(cartFields);
    var line = 0;
    Object.keys(selected).forEach(function (handle) {
      if (!selected[handle]) return;
      var info = products[handle] || {};
      if (!info.id) return;
      var id = el('input');
      id.type = 'hidden';
      id.name = 'items[' + line + '][id]';
      id.value = String(info.id);
      var qty = el('input');
      qty.type = 'hidden';
      qty.name = 'items[' + line + '][quantity]';
      qty.value = '1';
      cartFields.appendChild(id);
      cartFields.appendChild(qty);
      line++;
    });
    if (cartButton) cartButton.disabled = line === 0;
  }

  /* The selection control. Toggling repaints its own card and the cart
     form — never the whole result, so nothing flickers and the routine is
     not recomputed. */
  function selectToggle(item, card) {
    var handle = item.handle;
    var label = copy('select-label');
    var btn = el('button', 'yu-qcard__toggle');
    btn.type = 'button';
    var mark = el('span', 'yu-qcard__mark');
    mark.setAttribute('aria-hidden', 'true');
    btn.appendChild(mark);
    btn.appendChild(el('span', null, label));
    btn.setAttribute('aria-label', label + ': ' + (facts(item).title || item.title || handle));

    function paint() {
      var on = !!selected[handle];
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      card.classList.toggle('is-selected', on);
      card.classList.toggle('is-deselected', !on);
    }

    if (!purchasable(item)) {
      btn.disabled = true;
      card.classList.add('is-unavailable');
    } else {
      btn.addEventListener('click', function () {
        selected[handle] = !selected[handle];
        paint();
        syncCart();
      });
    }
    paint();
    return btn;
  }

  /* A real product card: image, title, price, ingredient badge — all of it
     from the map. `stepNumber` is null for the weekly add-on, which is an
     addition rather than a routine step (ruling 2). */
  function productCard(item, stepNumber) {
    var info = facts(item);
    var href = info.url || '';
    var card = el('div', 'yu-card yu-qcard');
    card.setAttribute('data-yu-qcard', item.handle);

    var mediaWrap = el('a', 'yu-card__mediawrap');
    mediaWrap.href = href;
    mediaWrap.setAttribute('tabindex', '-1');
    mediaWrap.setAttribute('aria-hidden', 'true');
    var media = el('div', 'yu-card__media yu-card__media--1-1');
    if (info.image) {
      var img = el('img');
      img.src = info.image;
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    }
    mediaWrap.appendChild(media);
    card.appendChild(mediaWrap);

    var body = el('div', 'yu-card__body');
    if (stepNumber) {
      body.appendChild(el('p', 'yu-qcard__step', copy('step-label') + ' ' + stepNumber));
    }

    var title = el('h3', 'yu-card__title');
    var titleLink = el('a', null, info.title || item.title || item.handle);
    titleLink.href = href;
    title.appendChild(titleLink);
    body.appendChild(title);

    var foot = el('div', 'yu-card__foot');
    if (info.price) foot.appendChild(el('span', 'yu-card__price', info.price));
    /* The badge is tinted by hero_key through the existing badge classes —
       no accent token and no ingredient name is decided here. The label is
       hero_label, the mapped FAMILY label the section bakes into the product
       map (R6-5) — the engine renders it without knowing the map. */
    if (info.hero_key && info.hero_label) {
      foot.appendChild(el('span', 'yu-badge yu-badge--' + info.hero_key, info.hero_label));
    }
    body.appendChild(foot);

    body.appendChild(selectToggle(item, card));
    card.appendChild(body);
    return card;
  }

  /* R5-7b — the add-on is the SAME card as a routine product: one uniform
     card size across the whole result, one meta row (price left, badge
     right), one selection control. It stays an addition rather than a fourth
     routine step (ruling 2) through its own labelled group and the absence
     of a step number — `productCard` skips the step line when stepNumber is
     null — not through a bespoke card shape. */
  function addonCard(item) {
    return productCard(item, null);
  }

  /* Primary action. A real form with real hidden line items, so the
     submission travels the theme's own [data-yu-add-form] path: ONE
     /cart/add.js call carrying every selected line, the cart drawer
     re-rendered from the bundled section response and opened, and a native
     POST as the fallback if the fetch fails. */
  function cartForm() {
    var form = el('form', 'yuk-quiz__cart');
    form.method = 'post';
    form.action = cfg.cartAddUrl;
    form.setAttribute('data-yu-add-form', '');
    cartFields = el('div', 'yuk-quiz__cartfields');
    cartFields.hidden = true;
    form.appendChild(cartFields);
    cartButton = el('button', 'yu-btn yu-btn--primary', copy('cart-label'));
    cartButton.type = 'submit';
    form.appendChild(cartButton);
    return form;
  }

  function renderResult() {
    cartFields = null;
    cartButton = null;
    if (!currentResult) {
      currentResult = buildResult();
      initSelection(currentResult);
    }
    var result = currentResult;
    var card = el('div', 'yuk-quiz__card yuk-quiz__card--wide yuk-quiz__result');

    if (!result) {
      card.appendChild(el('p', null, copy('result-fallback')));
      card.appendChild(resultNav());
      return card;
    }

    card.appendChild(el('h2', null, copy('result-heading')));
    if (result.track.result) {
      card.appendChild(el('p', 'yuk-quiz__resultcopy', result.track.result));
    }

    var grid = el('div', 'yuk-quiz__products');
    result.products.forEach(function (item, i) {
      grid.appendChild(productCard(item, i + 1));
    });
    card.appendChild(grid);

    /* Weekly-mask add-on — an addition, not a fourth routine step (ruling 2).
       Its cards go in their own scroll row so they match the routine cards
       exactly in size and behavior (R5-7b/c) while the group's hairline,
       label and note keep it clearly separate. */
    if (result.addons.length) {
      var addon = el('div', 'yuk-quiz__addon');
      addon.appendChild(el('p', 'yuk-quiz__addonlabel', copy('addon-label')));
      var note = copy('addon-note');
      if (note) addon.appendChild(el('p', 'yuk-quiz__addonnote', note));
      var addonRow = el('div', 'yuk-quiz__products');
      result.addons.forEach(function (item) { addonRow.appendChild(addonCard(item)); });
      addon.appendChild(addonRow);
      card.appendChild(addon);
    }

    card.appendChild(resultNav(true));
    syncCart();
    return card;
  }

  function resultNav(withCart) {
    var nav = el('div', 'yuk-quiz__nav');
    if (withCart) nav.appendChild(cartForm());
    var restart = el('button', 'yu-btn yu-btn--secondary', copy('retake-label'));
    restart.type = 'button';
    restart.addEventListener('click', function () {
      answers = {};
      step = 0;
      gateDone = false;
      currentResult = null;
      selected = {};
      cartFields = null;
      cartButton = null;
      render();
    });
    nav.appendChild(restart);
    return nav;
  }

  /* Results render client-side into the section with no page reload. */
  function render() {
    clear(root);
    /* The old cart form has just been detached — drop the handles so a
       stale selection toggle can never write into a dead node. */
    cartFields = null;
    cartButton = null;
    var total = matrix.questions.length;
    if (step < total) {
      root.appendChild(renderQuestion());
    } else if (cfg.gateEnabled && !gateDone) {
      root.appendChild(renderGate());
    } else {
      root.appendChild(renderResult());
    }
  }

  /* ---------- boot ---------- */
  if (!cfg.matrixUrl) return;
  fetch(cfg.matrixUrl, { headers: { 'Accept': 'application/json' } })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      matrix = data;
      if (!matrix || !matrix.questions || !matrix.questions.length) return;
      render();
    })
    .catch(function () {
      /* The section ships a no-JS message that stays visible on failure. */
    });
})();
